import { Loadable } from '@/components/loaders';
import RouteConstants from '@/features/router/RouteConstants';
import { CreateStepper, useCreateStepper } from '@/lib/Steppers';
import { DataSource, DataSourceIDEnum } from '@/models/dataSource';
import { FC, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CREATE_DATA_SOURCE_SOURCE_CONNECTOR, CREATE_DATA_SOURCE_SOURCE_CONNECTOR_STEPS } from './constants';
import {
  SourceConnectorColumnMapStep,
  SourceConnectorConfirmationStep,
  SourceConnectorEntityStep,
  SourceConnectorPreviewStep,
  SourceConnectorSelectionStep,
} from './steps';
import { initialSourceConnectorWizardData, SourceConnectorWizardData } from './types';
import { useSourceConnectorWizardBootstrap, isSourceConnectorBootstrapError, isSourceConnectorBootstrapReady } from './useSourceConnectorWizardBootstrap';

type CreateDataSourceSourceConnectorContentProps = {
  dataSourceId: DataSourceIDEnum;
  modalProps?: {
    onSuccess: (dataSource: DataSource) => void;
    onSuccessAndContinue?: (dataSource: DataSource) => void;
    closeModal: VoidFunction;
  };
};

type SourceConnectorWizardShellProps = {
  dataSourceId: DataSourceIDEnum;
  modalProps?: CreateDataSourceSourceConnectorContentProps['modalProps'];
  initialWizardData: SourceConnectorWizardData;
  initialStepId?: CREATE_DATA_SOURCE_SOURCE_CONNECTOR;
  fromCatalogManage?: boolean;
};

const SourceConnectorWizardShell: FC<SourceConnectorWizardShellProps> = ({
  dataSourceId,
  modalProps,
  initialWizardData,
  initialStepId,
  fromCatalogManage = false,
}) => {
  const history = useHistory();
  const { steps, currentStepId, currentStepIndex, goToNextStep, goToPreviousStep, completeCurrentStep } = useCreateStepper(
    CREATE_DATA_SOURCE_SOURCE_CONNECTOR_STEPS,
    initialStepId
  );

  const [wizardData, setWizardData] = useState<SourceConnectorWizardData>(initialWizardData);

  const updateWizardData = (partial: Partial<SourceConnectorWizardData>) => {
    setWizardData((prev) => ({ ...prev, ...partial }));
  };

  const handleNext = (partial?: Partial<SourceConnectorWizardData>) => {
    if (partial) updateWizardData(partial);
    completeCurrentStep();
    goToNextStep();
  };

  const handleCancel = () => {
    if (modalProps) {
      modalProps.closeModal();
    } else {
      history.push(RouteConstants.dataSourcesCatalog);
    }
  };

  const handleEntityPrev = () => {
    goToPreviousStep();
  };

  const handleCloseAndContinue = () => {
    const created = wizardData.createdDataSource;
    if (modalProps && created) {
      if (modalProps.onSuccessAndContinue) {
        modalProps.onSuccessAndContinue(created);
      } else {
        modalProps.onSuccess(created);
      }
    } else {
      history.push(RouteConstants.audiencesCreate);
    }
  };

  const handleCloseOnly = () => {
    const created = wizardData.createdDataSource;
    if (modalProps && created) {
      modalProps.onSuccess(created);
    } else {
      history.push(RouteConstants.dataSources);
    }
  };

  const stepTemplate = () => {
    switch (currentStepId) {
      case CREATE_DATA_SOURCE_SOURCE_CONNECTOR.CONNECTOR:
        return (
          <SourceConnectorSelectionStep
            wizardData={wizardData}
            fromCatalogManage={fromCatalogManage}
            onNext={(data) => handleNext(data)}
            onCancel={handleCancel}
          />
        );
      case CREATE_DATA_SOURCE_SOURCE_CONNECTOR.ENTITY:
        return (
          <SourceConnectorEntityStep
            wizardData={wizardData}
            onNext={(data) => handleNext(data)}
            onPrev={handleEntityPrev}
            onCancel={handleCancel}
          />
        );
      case CREATE_DATA_SOURCE_SOURCE_CONNECTOR.PREVIEW:
        return <SourceConnectorPreviewStep wizardData={wizardData} onNext={(data) => handleNext(data)} onPrev={goToPreviousStep} onCancel={handleCancel} />;
      case CREATE_DATA_SOURCE_SOURCE_CONNECTOR.MAP_COLUMNS:
        return (
          <SourceConnectorColumnMapStep
            wizardData={wizardData}
            dataSourceTypeId={dataSourceId}
            onNext={(data) => handleNext(data)}
            onPrev={goToPreviousStep}
            onCancel={handleCancel}
          />
        );
      case CREATE_DATA_SOURCE_SOURCE_CONNECTOR.CONFIRMATION:
        return (
          <SourceConnectorConfirmationStep
            onInit={completeCurrentStep}
            onNextStep={handleCloseAndContinue}
            onPrevStep={handleCloseOnly}
            isModal={!!modalProps}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <CreateStepper steps={steps} currentStepIndex={currentStepIndex} />
      {stepTemplate()}
    </>
  );
};

export const CreateDataSourceSourceConnectorContent: FC<CreateDataSourceSourceConnectorContentProps> = ({ dataSourceId, modalProps }) => {
  const { t } = useTranslation('data_sources');
  const bootstrap = useSourceConnectorWizardBootstrap();

  if (bootstrap.isSeeded) {
    if (bootstrap.isLoading) {
      return <Loadable isLoading loadingMessage='data_sources.source_connectors.preparing_wizard' />;
    }

    if (isSourceConnectorBootstrapError(bootstrap)) {
      return (
        <div className='flex max-w-xl flex-col gap-4'>
          <p className='text-sm text-secondary-700'>{t('wizard.source_connector.no_authenticated_connectors')}</p>
          <Link to={RouteConstants.dataSourcesCatalog} className='text-sm font-semibold text-brand-600 hover:underline'>
            {t('wizard.source_connector.go_to_catalog')}
          </Link>
        </div>
      );
    }

    if (isSourceConnectorBootstrapReady(bootstrap)) {
      return (
        <SourceConnectorWizardShell
          dataSourceId={dataSourceId}
          modalProps={modalProps}
          initialWizardData={{ ...initialSourceConnectorWizardData(), ...bootstrap.wizardData }}
          initialStepId={bootstrap.initialStepId}
          fromCatalogManage={bootstrap.fromCatalogManage}
        />
      );
    }
  }

  return <SourceConnectorWizardShell dataSourceId={dataSourceId} modalProps={modalProps} initialWizardData={initialSourceConnectorWizardData()} />;
};
